import React, { useRef, useCallback, useEffect, useState } from 'react';

// Horizontal position page 1, 2 products are triggered
const SCROLL_TRIGGER_PAGE_1_INDEX = 75;
const SCROLL_TRIGGER_PAGE_2_INDEX = 750;

interface ExtendedProductsScrollPaginationProps {
  handleScrollPagination: Function;
  extendedPage: number;
  totalProducts: number;
}

interface ExtendedProductsScrollPaginationResult {
  ref: React.RefObject<HTMLDivElement>;
  isScrollable: boolean;
}

// Handles scroll-to-paginate logic for exetended product rows
const useExtendedProductsScrollPagination = ({
  handleScrollPagination,
  extendedPage,
  totalProducts,
}: ExtendedProductsScrollPaginationProps): ExtendedProductsScrollPaginationResult => {
  const productRowRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  // Handle extended carousel pagination
  const scrollPaginate = useCallback(
    (isForward: boolean) => {
      if (isScrollable && (extendedPage === 0 || extendedPage === 1)) {
        handleScrollPagination(isForward);
      }
    },
    [isScrollable, extendedPage, handleScrollPagination],
  );

  // Handle horizontal scroll triggering extended products
  const handleMobileExtendedCarouselTrigger = useCallback(() => {
    if (
      productRowRef?.current &&
      productRowRef?.current?.getElementsByTagName('ul')?.length > 0 &&
      productRowRef?.current?.getElementsByTagName('ul')[0]?.scrollLeft >=
        SCROLL_TRIGGER_PAGE_1_INDEX &&
      productRowRef?.current?.getElementsByTagName('ul')[0]?.scrollLeft <
        SCROLL_TRIGGER_PAGE_2_INDEX &&
      extendedPage === 0 &&
      totalProducts > 6
    ) {
      scrollPaginate(true);
    } else if (
      productRowRef?.current?.getElementsByTagName('ul')[0]?.scrollLeft &&
      productRowRef?.current?.getElementsByTagName('ul')[0]?.scrollLeft >=
        SCROLL_TRIGGER_PAGE_2_INDEX &&
      extendedPage === 1 &&
      totalProducts > 12 &&
      productRowRef?.current
        ?.getElementsByTagName('ul')[0]
        ?.getElementsByTagName('li')?.length > 6
    ) {
      scrollPaginate(true);
    }
  }, [extendedPage, handleScrollPagination]);

  // Update isScrollable if horizontal overflow detected
  useEffect(() => {
    if (
      !isScrollable &&
      productRowRef?.current &&
      productRowRef?.current?.getElementsByTagName('ul')?.length > 0 &&
      productRowRef?.current?.getElementsByTagName('ul')[0]?.scrollWidth >
        productRowRef?.current?.getElementsByTagName('ul')[0]?.clientWidth
    ) {
      setIsScrollable(true);
    } else if (
      isScrollable &&
      productRowRef?.current &&
      productRowRef?.current?.getElementsByTagName('ul')?.length > 0 &&
      productRowRef?.current?.getElementsByTagName('ul')[0]?.clientWidth >= 1296
    ) {
      setIsScrollable(false);
    }
  }, [
    isScrollable,
    productRowRef?.current?.getElementsByTagName('ul')[0]?.scrollWidth,
    productRowRef?.current?.getElementsByTagName('ul')[0]?.clientWidth,
  ]);

  // Attaches scroll event lister, handles triggering extended products for scrollable carousels
  useEffect(() => {
    if (productRowRef?.current?.getElementsByTagName('ul')[0]) {
      productRowRef?.current
        ?.getElementsByTagName('ul')[0]
        .addEventListener('scroll', handleMobileExtendedCarouselTrigger);
    }

    return (): void => {
      productRowRef?.current
        ?.getElementsByTagName('ul')[0]
        .removeEventListener('scroll', handleMobileExtendedCarouselTrigger);
    };
  }, [productRowRef?.current, handleMobileExtendedCarouselTrigger]);

  return {
    ref: productRowRef,
    isScrollable,
  };
};

export default useExtendedProductsScrollPagination;
